import React, { useState } from 'react';
import { Flame, Skull, Zap, Database, Search } from 'lucide-react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';


const HazardLibrary = ({ searchQuery }) => {
  const [selectedCategory, setSelectedCategory] = useState('flammability');
  const [librarySearch, setLibrarySearch] = useState('');

  const categories = [
    { id: 'flammability', label: 'Flammability', icon: Flame, color: 'text-red-600' },
    { id: 'toxicity', label: 'Toxicity', icon: Skull, color: 'text-purple-600' },
    { id: 'energy', label: 'Energy Sources', icon: Zap, color: 'text-yellow-600' },
  ];

  const hazardLibrary = {
    flammability: [
      {
        id: 'FLAM-001',
        name: 'Hydrogen Gas',
        description: 'Extremely flammable gas, wide flammability range (4-75% in air)',
        hazardClass: 'Category 1A',
        flashPoint: 'Gas',
        autoIgnitionTemp: '500°C',
        flammabilityLimits: { lower: '4%', upper: '75%' },
        typicalSources: ['Process units', 'Hydrocracking', 'Reforming units'],
        controlMeasures: [
          'Gas detection systems',
          'Proper ventilation',
          'Explosion-proof equipment',
          'No ignition sources',
        ],
        emergencyResponse: [
          'Evacuate area',
          'Shut off gas supply',
          'Use water spray to disperse',
          'Allow controlled burn if safe',
        ],
      },
      {
        id: 'FLAM-002',
        name: 'Crude Oil',
        description: 'Flammable liquid mixture of hydrocarbons',
        hazardClass: 'Category 2',
        flashPoint: '-18°C to 60°C',
        autoIgnitionTemp: '250-450°C',
        flammabilityLimits: { lower: '1%', upper: '6%' },
        typicalSources: ['Storage tanks', 'Process vessels', 'Transfer operations'],
        controlMeasures: [
          'Bonding and grounding',
          'Vapor recovery systems',
          'Tank farm design',
          'Hot work controls',
        ],
        emergencyResponse: [
          'Use foam extinguishers',
          'Cool adjacent tanks',
          'Prevent spread with berms',
          'Notify emergency services',
        ],
      },
    ],
    toxicity: [
      {
        id: 'TOX-001',
        name: 'Hydrogen Sulfide (H2S)',
        description: 'Highly toxic gas with characteristic rotten egg odor at low concentrations',
        hazardClass: 'Category 2',
        exposureLimit: {
          twa: '10 ppm',
          stel: '15 ppm',
          idlh: '100 ppm',
        },
        healthEffects: {
          acute: ['Respiratory paralysis', 'Loss of consciousness', 'Death'],
          chronic: ['Eye damage', 'Respiratory irritation', 'Neurological effects'],
        },
        typicalSources: ['Sour gas processing', 'Refinery operations', 'Wastewater treatment'],
        controlMeasures: [
          'Continuous gas monitoring',
          'Proper ventilation',
          'Respiratory protection',
          'Buddy system',
          'Emergency escape respirators',
        ],
        emergencyResponse: [
          'Move to fresh air immediately',
          'Administer oxygen',
          'Seek medical attention',
          'Decontaminate affected area',
        ],
      },
      {
        id: 'TOX-002',
        name: 'Benzene',
        description: 'Carcinogenic aromatic hydrocarbon, sweet odor',
        hazardClass: 'Category 1A',
        exposureLimit: {
          twa: '0.5 ppm',
          stel: '2.5 ppm',
          idlh: '500 ppm',
        },
        healthEffects: {
          acute: ['Dizziness', 'Headache', 'CNS depression'],
          chronic: ['Leukemia', 'Blood disorders', 'Bone marrow damage'],
        },
        typicalSources: ['Reformer units', 'Storage', 'Loading operations'],
        controlMeasures: [
          'Closed system handling',
          'Air monitoring',
          'PPE requirement',
          'Medical surveillance',
        ],
        emergencyResponse: [
          'Remove from exposure',
          'Wash contaminated skin',
          'Medical evaluation',
          'Report exposure incident',
        ],
      },
    ],
    energy: [
      {
        id: 'ENERGY-001',
        name: 'High Pressure Systems',
        description: 'Compressed gases and liquids under pressure',
        pressureRange: '100-10000 psig',
        energyHazards: [
          'Rapid decompression',
          'Projectile release',
          'Pressure vessel rupture',
          'Pipe whip',
        ],
        typicalSources: ['Compressors', 'High-pressure vessels', 'Hydraulic systems'],
        controlMeasures: [
          'Pressure relief systems',
          'Regular inspection',
          'Proper isolation procedures',
          'Safe work distance',
        ],
        emergencyResponse: [
          'Emergency shutdown',
          'Evacuate blast radius',
          'Control access',
          'Assess damage before restart',
        ],
      },
      {
        id: 'ENERGY-002',
        name: 'Electrical Energy',
        description: 'Electrical power systems and equipment',
        voltageRange: '120V-13.8kV',
        energyHazards: [
          'Electrocution',
          'Arc flash',
          'Burns',
          'Falls from shock',
        ],
        typicalSources: ['Power distribution', 'Motors', 'Control systems', 'Lighting'],
        controlMeasures: [
          'LOTO procedures',
          'Qualified personnel only',
          'PPE requirements',
          'Arc flash analysis',
          'Proper clearances',
        ],
        emergencyResponse: [
          'De-energize system',
          'Rescue procedures',
          'First aid/CPR',
          'Report to safety',
        ],
      },
      {
        id: 'ENERGY-003',
        name: 'Thermal Energy',
        description: 'High temperature equipment and processes',
        temperatureRange: '200-1500°F',
        energyHazards: [
          'Burns',
          'Heat stress',
          'Fire initiation',
          'Material degradation',
        ],
        typicalSources: ['Heaters', 'Furnaces', 'Steam systems', 'Hot process streams'],
        controlMeasures: [
          'Insulation',
          'Thermal protection clothing',
          'Safe approach distances',
          'Cool-down procedures',
        ],
        emergencyResponse: [
          'Cool affected area',
          'Burn treatment',
          'Medical evaluation',
          'Incident investigation',
        ],
      },
    ],
  };

  const currentLibrary = hazardLibrary?.[selectedCategory] || [];

  const filteredLibrary = currentLibrary?.filter(item => {
    const search = librarySearch?.toLowerCase();
    return search === '' ||
      item?.name?.toLowerCase()?.includes(search) ||
      item?.description?.toLowerCase()?.includes(search) ||
      item?.id?.toLowerCase()?.includes(search);
  });

  return (
    <div className="space-y-6">
      {/* Category Selection */}
      <div className="flex flex-wrap gap-3">
        {categories?.map((category) => {
          const Icon = category?.icon;
          return (
            <button
              key={category?.id}
              onClick={() => setSelectedCategory(category?.id)}
              className={`
                flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all
                ${selectedCategory === category?.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }
              `}
            >
              <Icon className={`w-5 h-5 ${selectedCategory === category?.id ? 'text-white' : category?.color}`} />
              {category?.label}
            </button>
          );
        })}
      </div>
      {/* Library Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <Input
          type="text"
          placeholder="Search hazard library..."
          value={librarySearch}
          onChange={(e) => setLibrarySearch(e?.target?.value)}
          icon={<Search className="w-5 h-5 text-gray-400" />}
        />
      </div>
      {/* Hazard Library Items */}
      <div className="space-y-6">
        {filteredLibrary?.map((item) => {
          const CategoryIcon = categories?.find(c => c?.id === selectedCategory)?.icon || Database;
          
          return (
            <div key={item?.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-blue-200">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <CategoryIcon className={`w-6 h-6 ${categories?.find(c => c?.id === selectedCategory)?.color}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{item?.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{item?.id}</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
                    {item?.hazardClass}
                  </span>
                </div>
                <p className="text-sm text-gray-700 mt-3">{item?.description}</p>
              </div>
              {/* Content */}
              <div className="px-6 py-4 space-y-4">
                {/* Category-specific details */}
                {selectedCategory === 'flammability' && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="p-4 bg-red-50 rounded-lg">
                      <div className="text-xs font-medium text-gray-600 mb-1">Flash Point</div>
                      <div className="text-lg font-bold text-red-800">{item?.flashPoint}</div>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg">
                      <div className="text-xs font-medium text-gray-600 mb-1">Auto-Ignition Temp</div>
                      <div className="text-lg font-bold text-orange-800">{item?.autoIgnitionTemp}</div>
                    </div>
                    <div className="p-4 bg-yellow-50 rounded-lg">
                      <div className="text-xs font-medium text-gray-600 mb-1">Flammability Limits</div>
                      <div className="text-lg font-bold text-yellow-800">
                        {item?.flammabilityLimits?.lower} - {item?.flammabilityLimits?.upper}
                      </div>
                    </div>
                  </div>
                )}

                {selectedCategory === 'toxicity' && (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <div className="text-xs font-medium text-gray-600 mb-1">TWA Limit</div>
                        <div className="text-lg font-bold text-purple-800">{item?.exposureLimit?.twa}</div>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <div className="text-xs font-medium text-gray-600 mb-1">STEL</div>
                        <div className="text-lg font-bold text-purple-800">{item?.exposureLimit?.stel}</div>
                      </div>
                      <div className="p-4 bg-red-50 rounded-lg">
                        <div className="text-xs font-medium text-gray-600 mb-1">IDLH</div>
                        <div className="text-lg font-bold text-red-800">{item?.exposureLimit?.idlh}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-semibold text-gray-900 mb-2">Acute Health Effects</div>
                        <ul className="space-y-1">
                          {item?.healthEffects?.acute?.map((effect, idx) => (
                            <li key={idx} className="text-sm text-gray-700 flex items-start">
                              <span className="text-red-500 mr-2">•</span>
                              {effect}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900 mb-2">Chronic Health Effects</div>
                        <ul className="space-y-1">
                          {item?.healthEffects?.chronic?.map((effect, idx) => (
                            <li key={idx} className="text-sm text-gray-700 flex items-start">
                              <span className="text-orange-500 mr-2">•</span>
                              {effect}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </>
                )}

                {selectedCategory === 'energy' && (
                  <>
                    <div className="p-4 bg-yellow-50 rounded-lg">
                      <div className="text-xs font-medium text-gray-600 mb-1">
                        {item?.pressureRange ? 'Pressure Range' : item?.voltageRange ? 'Voltage Range' : 'Temperature Range'}
                      </div>
                      <div className="text-lg font-bold text-yellow-800">
                        {item?.pressureRange || item?.voltageRange || item?.temperatureRange}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900 mb-2">Energy Hazards</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {item?.energyHazards?.map((hazard, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-red-50 rounded text-sm text-red-800">
                            <Zap className="w-4 h-4" />
                            {hazard}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Common sections */}
                <div>
                  <div className="text-sm font-semibold text-gray-900 mb-2">Typical Sources</div>
                  <div className="flex flex-wrap gap-2">
                    {item?.typicalSources?.map((source, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full border border-blue-200">
                        {source}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-900 mb-2">Control Measures</div>
                    <ul className="space-y-2">
                      {item?.controlMeasures?.map((measure, idx) => (
                        <li key={idx} className="text-sm text-gray-700 flex items-start">
                          <span className="text-green-500 mr-2">✓</span>
                          {measure}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900 mb-2">Emergency Response</div>
                    <ul className="space-y-2">
                      {item?.emergencyResponse?.map((response, idx) => (
                        <li key={idx} className="text-sm text-gray-700 flex items-start">
                          <span className="text-orange-500 mr-2">!</span>
                          {response}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HazardLibrary;